import { initContract } from "@ts-rest/core";
import z from "zod";
import {
    EntityProfileIndustry,
    EntityProfileType,
    BusinessTaxReturns,
    FirmSize,
} from "@common/types/entity";
import { OrganizationServicesOffered } from "@common/types/organization";
import {
    ErrorMessageSchema,
    ErrorResponseSchema,
} from "@common/schemas/error.schema";
import {
    OrganizationDtoSchema,
    OrganizationsListResponseSchema,
    OrganizationProfileSchema,
} from "../schemas/organization.schema";
import { WorkspaceTimezone } from "../workspaceTimezone";

const c = initContract();

export const organizationContract = c.router(
    {
        getOrganization: {
            method: "GET",
            path: "/",
            responses: {
                200: OrganizationDtoSchema.nullable(),
                500: ErrorResponseSchema,
            },
        },
        listOrganizations: {
            method: "GET",
            path: "/list",
            responses: {
                200: OrganizationsListResponseSchema,
                401: ErrorMessageSchema,
                500: ErrorResponseSchema,
            },
        },
        createOrganization: {
            method: "POST",
            path: "/",
            body: z.object({
                name: z.string(),
                profile: z.object({
                    firmSize: z.nativeEnum(FirmSize),
                    businessTaxReturns: z
                        .nativeEnum(BusinessTaxReturns)
                        .optional(),
                    servicesOffered: z.array(
                        z.nativeEnum(OrganizationServicesOffered),
                    ),
                }),
                client: z.object({
                    name: z.string(),
                    legalType: z.nativeEnum(EntityProfileType),
                    bookkeepingStartDate: z.string().date(),
                    timezone: z.nativeEnum(WorkspaceTimezone).optional(),
                }),
            }),
            responses: {
                201: OrganizationDtoSchema,
            },
        },
        createOrganizationForOnboarding: {
            method: "POST",
            path: "/onboarding",
            body: z.object({
                name: z.string(),
                profile: z
                    .object({
                        firmSize: z.nativeEnum(FirmSize),
                        businessTaxReturns: z
                            .nativeEnum(BusinessTaxReturns)
                            .optional(),
                        servicesOffered: z.array(
                            z.nativeEnum(OrganizationServicesOffered),
                        ),
                        industries: z
                            .array(z.nativeEnum(EntityProfileIndustry))
                            .max(50)
                            .optional(),
                    })
                    .optional(),
                clientCountries: z
                    .array(z.string().length(2).toUpperCase())
                    .optional(),
            }),
            responses: {
                201: OrganizationDtoSchema,
                400: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        // this is called during onboarding
        updateOrganization: {
            method: "PUT",
            path: "/:organizationId",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            body: z.object({
                name: z.string().optional(),
                logoUrl: z.string().nullable().optional(),
                profile: z
                    .object({
                        firmSize: z.nativeEnum(FirmSize),
                        businessTaxReturns: z
                            .nativeEnum(BusinessTaxReturns)
                            .optional(),
                        servicesOffered: z.array(
                            z.nativeEnum(OrganizationServicesOffered),
                        ),
                        industries: z
                            .array(z.nativeEnum(EntityProfileIndustry))
                            .max(50)
                            .optional(),
                    })
                    .optional(),
                clientCountries: z
                    .array(z.string().length(2).toUpperCase())
                    .optional(),
            }),
            responses: {
                200: OrganizationDtoSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
        updateOrganizationProfile: {
            method: "PUT",
            path: "/:organizationId/profile",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            body: OrganizationProfileSchema,
            responses: {
                200: OrganizationDtoSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createLogoUploadUrl: {
            method: "POST",
            path: "/:organizationId/logo/upload-url",
            pathParams: z.object({
                organizationId: z.string().uuid(),
            }),
            body: z.object({
                name: z.string(),
            }),
            responses: {
                201: z.object({
                    url: z.string(),
                    path: z.string(),
                }),
            },
        },
        removeUnonboardedOrganizations: {
            method: "DELETE",
            path: "/unonboarded",
            responses: {
                204: c.noBody(),
                401: ErrorMessageSchema,
                500: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/organization",
    },
);
